import * as React from 'react';
import { useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useAuthStore } from '@/stores/auth-store';
import { BreachCraftLogo } from '@/components/ui/logo';
import { NotificationDropdown } from '@/components/ui/notification-dropdown';
import { MobileNav } from './mobile-nav';
import { PartnerBottomNav, AdminBottomNav } from '@/components/ui/bottom-nav';
import { cn } from '@/lib/utils';

// MFA Warning Bar Component
function MfaWarningBar() {
  // Use selector to ensure re-render when mfa_enabled changes
  const mfaEnabled = useAuthStore((state) => state.user?.mfa_enabled);
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  // Don't show if:
  // - User has MFA enabled
  // - User dismissed the warning
  // - User is already on the MFA setup page
  if (mfaEnabled || dismissed || location.pathname === '/mfa-setup') {
    return null;
  }

  return (
    <div className="bg-amber-500 text-amber-900">
      <div className="container max-w-site mx-auto px-[4vw]">
        <div className="flex items-center justify-between py-2 gap-4">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <svg
              className="w-5 h-5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <p className="text-sm font-medium truncate">
              Your account is not protected by two-factor authentication.{' '}
              <Link
                to="/mfa-setup"
                className="underline font-semibold hover:no-underline"
              >
                Enable MFA now
              </Link>
            </p>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="p-1 hover:bg-amber-600/20 rounded transition-colors flex-shrink-0"
            aria-label="Dismiss"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { user, logout, hasRole } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const openMobileMenu = useCallback(() => {
    setMobileMenuOpen(true);
  }, []);

  const isAdmin = hasRole('global_admin', 'global_reader', 'channel_manager');

  // Admin users get admin-specific links for Dashboard, Deals, Commissions
  const navLinks = isAdmin
    ? [
        { href: '/admin/dashboard', label: 'Dashboard' },
        { href: '/admin/deals', label: 'Deals' },
        { href: '/admin/commissions', label: 'Commissions' },
        { href: '/admin/organizations', label: 'Partners' },
        { href: '/admin/customers', label: 'Customers' },
        { href: '/admin/users', label: 'Users' },
        { href: '/admin/offerings', label: 'Offerings' },
        { href: '/admin/resources', label: 'Resources' },
      ]
    : [
        { href: '/dashboard', label: 'Dashboard' },
        { href: '/deals', label: 'Deals' },
        { href: '/commissions', label: 'Commissions' },
        { href: '/resources', label: 'Knowledge Hub' },
        { href: '/tiers', label: 'Tiers' },
        ...(user?.organization_partner_type !== 'individual' ? [{ href: '/team', label: 'Team' }] : []),
      ];

  const isActive = (href: string) => {
    // For admin routes, check prefix match (e.g., /admin/deals should match /admin/deals/123)
    if (href.startsWith('/admin/')) {
      return location.pathname.startsWith(href);
    }
    // For regular routes, exact match or prefix match for detail pages
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* MFA Warning Banner */}
      <MfaWarningBar />

      {/* Navigation */}
      <nav className="bg-bc-navy sticky top-0 z-50">
        <div className="container max-w-site mx-auto px-[4vw]">
          <div className="flex items-center justify-between h-header md:h-header-mobile">
            {/* Logo */}
            <Link
              to="/dashboard"
              className="flex items-center gap-3 text-white font-bold text-lg no-underline hover:no-underline"
            >
              <BreachCraftLogo className="w-10 h-10" />
              <span className="hidden sm:inline">Partner Portal</span>
            </Link>

            {/* Desktop Navigation - with active state indicator */}
            <ul className="hidden lg:flex items-center gap-1 list-none m-0 p-0">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className={cn(
                      'relative px-3 py-2 text-sm font-medium transition-all duration-150 no-underline rounded-md',
                      isActive(link.href)
                        ? 'text-white after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-6 after:h-[3px] after:bg-bc-teal after:rounded-t'
                        : 'text-gray-300 hover:text-white hover:bg-bc-navy-light'
                    )}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* User Menu */}
            <div className="flex items-center gap-4">
              {/* Notifications */}
              <div className="hidden lg:block">
                <NotificationDropdown />
              </div>

              {/* Desktop User Dropdown */}
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button className="hidden lg:flex items-center gap-2 text-gray-300 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-bc-teal focus-visible:ring-offset-2 focus-visible:ring-offset-bc-navy rounded-md px-2 py-1">
                    <div className="w-8 h-8 rounded-full bg-bc-teal text-white flex items-center justify-center font-semibold text-sm">
                      {user?.first_name?.charAt(0) || 'U'}
                    </div>
                    <span className="text-sm font-medium">
                      {user?.first_name || 'User'}
                    </span>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content
                    className="min-w-[180px] bg-bc-navy-light rounded-md shadow-lg p-2 z-[110]"
                    sideOffset={8}
                    align="end"
                  >
                    <DropdownMenu.Item asChild>
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-bc-navy rounded-md no-underline transition-colors outline-none"
                      >
                        Profile
                      </Link>
                    </DropdownMenu.Item>
                    <DropdownMenu.Item asChild>
                      <Link
                        to="/change-password"
                        className="block px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-bc-navy rounded-md no-underline transition-colors outline-none"
                      >
                        Change Password
                      </Link>
                    </DropdownMenu.Item>
                    {hasRole('global_admin') && (
                      <>
                        <DropdownMenu.Separator className="h-px bg-gray-700 my-1" />
                        <DropdownMenu.Item asChild>
                          <Link
                            to="/admin/settings"
                            className="block px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-bc-navy rounded-md no-underline transition-colors outline-none"
                          >
                            System Settings
                          </Link>
                        </DropdownMenu.Item>
                      </>
                    )}
                    <DropdownMenu.Separator className="h-px bg-gray-700 my-1" />
                    <DropdownMenu.Item
                      onSelect={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-bc-navy rounded-md cursor-pointer transition-colors outline-none"
                    >
                      Sign Out
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>

              {/* Mobile Menu Button */}
              <MobileNav
                navLinks={navLinks}
                onLogout={handleLogout}
                open={mobileMenuOpen}
                onOpenChange={setMobileMenuOpen}
              />
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content - add padding-bottom for bottom nav on mobile */}
      <main id="main-content" className="flex-1 py-6 md:py-8 pb-24 sm:pb-8">
        <div className="container max-w-site mx-auto px-[4vw]">{children}</div>
      </main>

      {/* Footer - hidden on mobile when bottom nav is visible */}
      <footer className="hidden sm:flex bg-bc-black text-gray-400 h-header md:h-header-mobile mt-auto items-center">
        <div className="container max-w-site mx-auto px-[4vw]">
          <div className="flex flex-col md:flex-row justify-between items-center gap-2 md:gap-4">
            <p className="text-sm mb-0">
              &copy; {new Date().getFullYear()} Breach Craft LLC. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-sm text-gray-300 hover:text-bc-teal">
                Privacy Policy
              </Link>
              <a
                href="https://breachcraft.io"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm group"
              >
                <span className="text-gray-300 group-hover:text-bc-teal group-hover:underline transition-colors">breachcraft</span>
                <span className="text-bc-teal group-hover:text-white transition-colors">.io</span>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Bottom Navigation - mobile only */}
      {isAdmin ? (
        <AdminBottomNav onMenuClick={openMobileMenu} />
      ) : (
        <PartnerBottomNav onMenuClick={openMobileMenu} />
      )}
    </div>
  );
}
